import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Snackbar,
  Typography,
  Skeleton,
  Grid,
  IconButton,
  useTheme,
  Fade,
  Container,
  Stack,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import {
  Add,
  Edit,
  CheckCircle,
  DirectionsRun,
  Archive,
  Person,
  Close,
} from "@mui/icons-material";
import axios from "axios";
import Cookies from "universal-cookie";
import HandlerModal from "./Modal/component";

export type MyHandler = {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
};

const Handlers = (): JSX.Element => {
  const [handlers, setHandlers] = useState<MyHandler[]>([]);
  const [activeHandler, setActiveHandler] = useState<MyHandler | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<MyHandler | null>(null);
  const [archiving, setArchiving] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    getHandlers();
  }, []);

  const getHandlers = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = new Cookies().get("token");
      const response = await axios.get(
        "https://api.easyagility.co.uk/my-handlers",
        { headers: { Authorization: "Bearer " + token } },
      );
      setHandlers(response.data);
    } catch (err) {
      setError("Failed to load handlers. Please try again later.");
      console.error("Error fetching handlers:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (handler: MyHandler) => {
    setActiveHandler(handler);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setActiveHandler(null);
    setModalOpen(true);
  };

  const handleArchive = async () => {
    if (!archiveTarget) return;
    try {
      setArchiving(true);
      const token = new Cookies().get("token");
      await axios.post(
        "https://api.easyagility.co.uk/my-handlers/archive",
        { id: archiveTarget.id },
        { headers: { Authorization: "Bearer " + token } },
      );
      setArchiveTarget(null);
      await getHandlers();
    } catch (err) {
      console.error("Error archiving handler:", err);
      setError("Failed to archive handler. Please try again.");
    } finally {
      setArchiving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width="60%" height={48} />
          <Skeleton variant="text" width="40%" height={24} />
        </Box>
        <Grid container spacing={3}>
          {[...Array(4)]?.map((_, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card sx={{ height: 200 }}>
                <CardContent>
                  <Skeleton variant="text" width="80%" height={32} />
                  <Skeleton variant="text" width="60%" height={24} />
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={80}
                    sx={{ mt: 2 }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            color: theme.palette.primary.main,
            mb: 1,
            fontSize: { xs: "2rem", md: "2.5rem" },
          }}
        >
          My Handlers
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: { xs: "1rem", md: "1.1rem" },
            mb: 3,
          }}
        >
          Manage the handlers registered on your account
        </Typography>
      </Box>

      {/* Add Button */}
      <Box sx={{ mb: 4, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAdd}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: "none",
            fontSize: { xs: "0.9rem", md: "1rem" },
            backgroundColor: theme.palette.success.main,
            "&:hover": { backgroundColor: theme.palette.success.dark },
          }}
        >
          Add New Handler
        </Button>
      </Box>

      {/* Error */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 4, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={getHandlers}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Empty state */}
      {handlers?.length === 0 ? (
        <Card
          sx={{
            textAlign: "center",
            py: 8,
            px: 4,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 3,
            boxShadow: theme.shadows[2],
          }}
        >
          <DirectionsRun
            sx={{ fontSize: 64, color: theme.palette.text.disabled, mb: 2 }}
          />
          <Typography
            variant="h6"
            sx={{ color: theme.palette.text.secondary, mb: 1, fontWeight: 500 }}
          >
            No Handlers Added Yet
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.disabled, mb: 3 }}
          >
            Add a handler to associate them with your competition entries.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAdd}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              backgroundColor: theme.palette.success.main,
              "&:hover": { backgroundColor: theme.palette.success.dark },
            }}
          >
            Add Your First Handler
          </Button>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {handlers?.map((handler) => (
            <Grid item xs={12} md={6} lg={4} key={handler.id}>
              <Fade in timeout={300}>
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    boxShadow: theme.shadows[2],
                    border: `1px solid ${theme.palette.divider}`,
                    transition: "all 0.3s ease-in-out",
                    display: "flex",
                    flexDirection: "column",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: theme.shadows[8],
                    },
                  }}
                >
                  {/* Avatar section */}
                  <Box
                    sx={{
                      height: 140,
                      backgroundColor: theme.palette.primary.main,
                      backgroundImage:
                        "linear-gradient(135deg, #00395A 0%, #1a5a7a 100%)",
                      borderTopLeftRadius: 12,
                      borderTopRightRadius: 12,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 72,
                        height: 72,
                        bgcolor: "rgba(255,255,255,0.2)",
                        border: "3px solid rgba(255,255,255,0.5)",
                        fontSize: "1.8rem",
                        fontWeight: 700,
                        color: "white",
                      }}
                    >
                      {handler.first_name.charAt(0).toUpperCase()}
                      {handler.last_name.charAt(0).toUpperCase()}
                    </Avatar>
                    <IconButton
                      onClick={() => handleEdit(handler)}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        bgcolor: "rgba(255,255,255,0.15)",
                        color: "white",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Box>

                  <CardContent
                    sx={{
                      p: 3,
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 1.5,
                      }}
                    >
                      <Typography
                        variant="h5"
                        component="h2"
                        sx={{ fontWeight: 600, lineHeight: 1.3 }}
                      >
                        {handler.full_name}
                      </Typography>
                    </Box>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      sx={{ color: "text.secondary" }}
                    >
                      <Person fontSize="small" />
                      <Typography variant="body2">Handler</Typography>
                    </Stack>
                  </CardContent>

                  {/* Actions */}
                  <Box
                    sx={{
                      p: 2,
                      pt: 0,
                      display: "flex",
                      gap: 1,
                      borderTop: `1px solid ${theme.palette.divider}`,
                      mt: "auto",
                    }}
                  >
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={() => handleEdit(handler)}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        py: 1,
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        "&:hover": {
                          borderColor: theme.palette.primary.dark,
                          backgroundColor: theme.palette.primary.light + "10",
                        },
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="warning"
                      startIcon={<Archive />}
                      onClick={() => setArchiveTarget(handler)}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        py: 1,
                      }}
                    >
                      Archive
                    </Button>
                  </Box>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add/Edit Modal */}
      <HandlerModal
        open={modalOpen}
        setOpen={setModalOpen}
        handler={activeHandler}
        onSaved={() => {
          getHandlers();
          setSuccessOpen(true);
        }}
      />

      {/* Archive Confirmation Dialog */}
      <Dialog
        open={archiveTarget != null}
        onClose={() => !archiving && setArchiveTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Archive color="warning" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Archive Handler
              </Typography>
            </Box>
            <IconButton
              onClick={() => setArchiveTarget(null)}
              size="small"
              disabled={archiving}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            Are you sure you want to archive{" "}
            <strong>{archiveTarget?.full_name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Archived handlers will no longer appear on your account.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setArchiveTarget(null)} disabled={archiving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={archiving ? <CircularProgress size={18} /> : <Archive />}
            onClick={handleArchive}
            disabled={archiving}
          >
            {archiving ? "Archiving…" : "Archive Handler"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={successOpen}
        autoHideDuration={5000}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessOpen(false)}
          severity="success"
          icon={<CheckCircle />}
          sx={{ width: "100%", borderRadius: 2 }}
        >
          Handler details saved successfully.
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Handlers;
